import React, { useEffect, useMemo, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { getNotices } from '../lib/notice';

// Critical safety-notice modal for the public portal (GH #38).
//
// Shows the first not-yet-dismissed notice whose content is displayable in the
// visitor's language (falling back to English, then any available language).
// Dismissal is remembered per-notice-id in localStorage, so a closed notice does
// not reappear — but a newly published notice (new id) is shown again.
//
// The portal sets no cookies (compliance separation); localStorage only.
const DISMISS_KEY = 'eifu_dismissed_notices';

const readDismissed = () => {
  try {
    const arr = JSON.parse(localStorage.getItem(DISMISS_KEY));
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
};

// Best localized { title, body } for `lang`, else English, else any language.
const localize = (content, lang) => {
  if (!content || typeof content !== 'object') {
    return null;
  }
  const pick = content[lang] || content.en || content[Object.keys(content)[0]];
  if (!pick) {
    return null;
  }
  const title = (pick.title || '').trim();
  const body = (pick.body || '').trim();
  return title || body ? { title, body } : null;
};

const CriticalNoticeModal = () => {
  const { t, i18n } = useTranslation(['translation']);
  const [notices, setNotices] = useState([]);
  const [dismissed, setDismissed] = useState(readDismissed);

  useEffect(() => {
    getNotices().then(setNotices);
  }, []);

  // First undismissed notice with content displayable in the current language.
  const active = useMemo(
    () =>
      notices.find(
        (n) => n && n.id && !dismissed.includes(n.id) && localize(n.content, i18n.language)
      ),
    [notices, dismissed, i18n.language]
  );

  const view = active ? localize(active.content, i18n.language) : null;

  const onClose = () => {
    if (!active) {
      return;
    }
    const next = Array.from(new Set([...dismissed, active.id]));
    setDismissed(next);
    try {
      localStorage.setItem(DISMISS_KEY, JSON.stringify(next));
    } catch {
      // localStorage unavailable — dismiss for this session only.
    }
  };

  if (!active || !view) {
    return null;
  }

  return (
    <Modal show onHide={onClose} centered backdrop="static">
      <Modal.Header closeButton>
        {/* Inline styles override the marketing theme, which otherwise shrinks
            the modal title (.modal-title/.h4/.fs-5) to near-invisible. */}
        <Modal.Title
          className="d-flex align-items-center gap-2"
          style={{ fontSize: '1.15rem', fontWeight: 700, color: '#2b2f3a', lineHeight: 1.3 }}
        >
          <i className="uil uil-exclamation-triangle text-danger" style={{ fontSize: '1.3rem' }} />
          <span>{view.title || t('criticalNotice.badge')}</span>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ whiteSpace: 'pre-wrap' }}>{view.body}</Modal.Body>
      <Modal.Footer>
        <Button variant="primary" size="sm" onClick={onClose}>
          {t('criticalNotice.close')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CriticalNoticeModal;
